import PropTypes from 'prop-types';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ThemeToggle Component
 * 
 * A toggle button for switching between light and dark themes.
 * Features sun/moon icons with smooth transitions and full accessibility support.
 * 
 * @component
 * @example
 * // Icon variant (default)
 * <ThemeToggle />
 * 
 * // With custom size
 * <ThemeToggle size="lg" />
 */
const ThemeToggle = ({
  variant = 'icon',
  size = 'md',
  className = '',
}) => {
  const { theme, toggleTheme } = useTheme();

  // Size styles
  const sizeStyles = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  // Icon size based on button size
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Sun icon (for light theme)
  const SunIcon = () => (
    <svg
      className={`${iconSizes[size]} transition-transform duration-slow ease-in-out ${theme === 'light' ? 'rotate-0' : 'rotate-180'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );

  // Moon icon (for dark theme)
  const MoonIcon = () => (
    <svg
      className={`${iconSizes[size]} transition-transform duration-slow ease-in-out ${theme === 'dark' ? 'rotate-0' : '-rotate-180'}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  );

  const handleToggle = () => {
    toggleTheme();
    // Announce theme change to screen readers
    const announcement = theme === 'light' 
      ? 'Theme switched to dark mode' 
      : 'Theme switched to light mode';
    
    // Create temporary announcement element
    const announcer = document.createElement('div');
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.className = 'sr-only';
    announcer.textContent = announcement;
    document.body.appendChild(announcer);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcer);
    }, 1000);
  };

  const handleKeyDown = (e) => {
    // Support Space and Enter keys
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleToggle();
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={`
          ${sizeStyles[size]}
          flex items-center justify-center
          rounded-lg
          bg-light-secondary dark:bg-brand-surface
          text-light-text-primary dark:text-brand-text-primary
          hover:bg-light-elevated dark:hover:bg-brand-elevated
          focus:outline-none focus:ring-2 focus:ring-light-interactive-focus dark:focus:ring-white/30
          transition-all duration-slow ease-in-out
          ${className}
        `.trim().replace(/\s+/g, ' ')}
        role="switch"
        aria-checked={theme === 'dark'}
        aria-label="Toggle theme"
        title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        type="button"
      >
        {theme === 'light' ? <SunIcon /> : <MoonIcon />}
      </button>
    );
  }

  // Additional variants can be added here (switch, button)
  return null;
};

ThemeToggle.propTypes = {
  /** Toggle variant style */
  variant: PropTypes.oneOf(['icon', 'switch', 'button']),
  /** Toggle size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default ThemeToggle;
